import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import {
  Add,
  AttachFile,
  Cancel,
  CheckCircleOutline,
  Delete,
  Edit,
  GetApp,
  HighlightOff,
  Mic,
  Mood,
  Send,
} from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { format, isSameDay, parseISO } from "date-fns";
import clsx from "clsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import MarkdownWrapper from "../../components/MarkdownWrapper";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import chatBackgroundMask from "../../assets/background.svg";
import Audio from "../../components/Audio";
import ModalImageCors from "../../components/ModalImageCors";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";

let audioRecorder = null;
let audioChunks = [];
let audioStream = null;

const getSupportedAudioMimeType = () => {
  if (!window.MediaRecorder) return "";
  const mimeTypes = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  return mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || "";
};

const stopAudioTracks = () => {
  if (audioStream) {
    audioStream.getTracks().forEach(track => track.stop());
    audioStream = null;
  }
};

const resetAudioRecorder = () => {
  audioRecorder = null;
  audioChunks = [];
  stopAudioTracks();
};

const stopRecording = () =>
  new Promise((resolve, reject) => {
    if (!audioRecorder || audioRecorder.state === "inactive") {
      reject(new Error("Recorder not active"));
      return;
    }
    audioRecorder.onstop = () => {
      const mimeType = audioRecorder.mimeType || getSupportedAudioMimeType() || "audio/webm";
      const blob = new Blob(audioChunks, { type: mimeType });
      resetAudioRecorder();
      resolve(blob);
    };
    audioRecorder.onerror = event => {
      resetAudioRecorder();
      reject(event.error || new Error("Recorder error"));
    };
    audioRecorder.stop();
  });

const cancelRecording = () =>
  new Promise(resolve => {
    if (!audioRecorder || audioRecorder.state === "inactive") {
      resetAudioRecorder();
      resolve();
      return;
    }
    audioRecorder.onstop = () => {
      resetAudioRecorder();
      resolve();
    };
    audioRecorder.stop();
  });

const getAudioExtension = mimeType => {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  return "webm";
};

const MediaPreviewThumb = ({ file, className }) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!file || !file.type?.startsWith("image/")) {
      setUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (url) return <img src={url} alt="" className={className} />;
  return <div className={className}>{file?.name?.split(".").pop()?.toUpperCase() || "FILE"}</div>;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  chatContainer: {
    flex: 1,
    minHeight: 0,
    maxHeight: "75vh",
    overflow: "hidden",
  },
  listPanel: {
    height: "100%",
    maxHeight: "75vh",
    minHeight: 0,
    borderRight: "1px solid rgba(0, 0, 0, .1)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabs: {
    borderBottom: "1px solid rgba(0, 0, 0, .08)",
    flex: 1,
    minHeight: 0,
  },
  tab: {
    alignItems: "flex-start",
    minHeight: 54,
    textTransform: "none",
  },
  tabLabel: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    overflow: "hidden",
  },
  tabName: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tabMembers: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: 11,
    lineHeight: "16px",
  },
  chatPanel: {
    height: "100%",
    display: "flex",
    overflow: "hidden",
    maxHeight: "75vh",
    minHeight: "75vh",
    flexDirection: "column",
  },
  chatHeader: {
    flex: "0 0 56px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2),
    backgroundColor: "#eee",
    borderBottom: "1px solid rgba(0, 0, 0, .08)",
  },
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    backgroundColor: "#efeae2",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      maskImage: `url(${chatBackgroundMask})`,
      maskRepeat: "repeat",
      maskMode: "alpha",
      maskSize: "540px 960px",
      WebkitMaskImage: `url(${chatBackgroundMask})`,
      WebkitMaskRepeat: "repeat",
      WebkitMaskSize: "540px 960px",
    },
  },
  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: "50vh",
    position: "relative",
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 430,
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: "5px 80px 16px 7px",
    boxShadow: "0 1px 1px #b3b3b3",
  },
  messageRight: {
    marginLeft: 20,
    alignSelf: "flex-end",
    backgroundColor: "#dcf8c6",
    borderTopLeftRadius: 8,
    borderBottomRightRadius: 0,
  },
  author: {
    display: "block",
    color: "#18806d",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 3,
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 2,
    right: 7,
    color: "#999",
  },
  dailyTimestamp: {
    alignSelf: "center",
    backgroundColor: "#e1f3fb",
    margin: 10,
    borderRadius: 10,
    boxShadow: "0 1px 1px #b3b3b3",
    color: "#808888",
    padding: 8,
  },
  inputBar: {
    flex: "0 0 auto",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: "#eee",
    position: "relative",
  },
  uploadInput: {
    display: "none",
  },
  emojiBox: {
    position: "absolute",
    bottom: "100%",
    left: 8,
    zIndex: 2,
    marginBottom: 8,
    width: 320,
    maxWidth: "calc(100vw - 48px)",
    "& .emoji-mart": {
      width: "320px !important",
      maxWidth: "calc(100vw - 48px)",
    },
    [theme.breakpoints.down("xs")]: {
      left: 0,
      width: "calc(100vw - 32px)",
      "& .emoji-mart": {
        width: "calc(100vw - 32px) !important",
      },
    },
  },
  mediaPreview: {
    flex: "0 0 auto",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  },
  mediaPreviewPanel: {
    position: "relative",
    padding: "14px 54px 14px 18px",
    backgroundColor: "#f7f7f7",
  },
  mediaPreviewCancel: {
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 2,
  },
  mediaPreviewGrid: {
    display: "flex",
    alignItems: "stretch",
    gap: 12,
    overflowX: "auto",
    paddingLeft: 30,
    ...theme.scrollbarStyles,
  },
  mediaPreviewCard: {
    position: "relative",
    flex: "0 0 150px",
    width: 150,
  },
  mediaPreviewImage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 150,
    height: 150,
    borderRadius: 8,
    objectFit: "cover",
    backgroundColor: "rgba(0, 0, 0, 0.15)",
    color: "#455a64",
    fontWeight: 700,
    fontSize: 13,
  },
  mediaPreviewName: {
    margin: "6px 0 0",
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#4f5b62",
    fontSize: 13,
  },
  mediaPreviewDelete: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 2,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": {
      backgroundColor: "#fff",
    },
  },
  mediaPreviewAdd: {
    flex: "0 0 180px",
    minHeight: 178,
    border: "1px dashed rgba(0, 0, 0, 0.28)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    textAlign: "center",
    padding: 12,
  },
  mediaPreviewInputBar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#eee",
  },
  messageMedia: {
    objectFit: "cover",
    borderRadius: 8,
    width: "stretch",
    maxWidth: "100%",
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
  recorderWrapper: {
    display: "flex",
    alignItems: "center",
  },
  cancelAudioIcon: {
    color: "red",
  },
  sendAudioIcon: {
    color: "green",
  },
  input: {
    flex: 1,
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  empty: {
    height: "100%",
    minHeight: "75vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#777",
  },
  createContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    "& .MuiTextField-root": {
      width: "100%",
    },
    "& .MuiAutocomplete-root": {
      width: "100%",
    },
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "wrap",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  createDialogActions: {
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5, 2),
      flexDirection: "column-reverse",
      alignItems: "stretch",
      "& > *": {
        margin: 0,
      },
      "& > * + *": {
        marginBottom: theme.spacing(1),
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const incoming = action.payload || [];
    const nextMessages = [];
    incoming.forEach(message => {
      const index = state.findIndex(item => item.id === message.id);
      if (index !== -1) {
        state[index] = message;
      } else {
        nextMessages.push(message);
      }
    });
    return [...nextMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const message = action.payload;
    const index = state.findIndex(item => item.id === message.id);
    if (index !== -1) {
      state[index] = message;
    } else {
      state.push(message);
    }
    return [...state].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  if (action.type === "RESET") return [];
  return state;
};

const InternalChat = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [messages, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageBody, setMessageBody] = useState("");
  const [medias, setMedias] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newChatName, setNewChatName] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const lastMessageRef = useRef();

  const selectedChat = chats.find(chat => String(chat.id) === String(selectedChatId));
  const canManageChats = ["admin", "superadmin", "owner"].includes(user?.profile);

  const getChatMembers = chat =>
    (chat.users || [])
      .map(chatUser => chatUser.name)
      .filter(Boolean)
      .join(", ");

  useEffect(() => {
    const fetchChats = async () => {
      setLoadingChats(true);
      try {
        const { data } = await api.get("/internal-chats");
        setChats(data.chats || []);
        if (!selectedChatId && data.chats?.length) {
          setSelectedChatId(String(data.chats[0].id));
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingChats(false);
      }
    };

    fetchChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!createOpen && !editOpen) return;
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam: "", pageNumber: 1 },
        });
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUsers();
  }, [createOpen, editOpen]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [selectedChatId]);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedChatId) return;
      setLoadingMessages(true);
      try {
        const { data } = await api.get(
          `/internal-chats/${selectedChatId}/messages`,
          { params: { pageNumber } }
        );
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
        setHasMore(data.hasMore);
        if (pageNumber === 1) {
          setTimeout(() => lastMessageRef.current?.scrollIntoView({}), 100);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedChatId, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    if (selectedChatId) {
      socket.on("connect", () => socket.emit("joinInternalChat", selectedChatId));
    }

    socket.on("internalChat", data => {
      if (data.action === "delete") {
        setChats(prev => prev.filter(chat => chat.id !== data.chatId));
        setSelectedChatId(current =>
          String(current) === String(data.chatId) ? "" : current
        );
        return;
      }
      if (!data.chat) return;
      setChats(prev => [data.chat, ...prev.filter(chat => chat.id !== data.chat.id)]);
      setSelectedChatId(current => current || String(data.chat.id));
    });

    socket.on("internalMessage", data => {
      if (String(data.message?.internalChatId) !== String(selectedChatId)) return;
      dispatch({ type: "ADD_MESSAGE", payload: data.message });
      setTimeout(() => lastMessageRef.current?.scrollIntoView({}), 100);
    });

    return () => socket.disconnect();
  }, [selectedChatId]);

  const handleScroll = event => {
    if (!hasMore || loadingMessages) return;
    if (event.currentTarget.scrollTop < 50) {
      setPageNumber(prev => prev + 1);
    }
  };

  const handleCreateChat = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/internal-chats", {
        name: newChatName,
        userIds: selectedUsers.map(selectedUser => selectedUser.id),
      });
      setChats(prev => [data, ...prev.filter(chat => chat.id !== data.id)]);
      setSelectedChatId(String(data.id));
      setNewChatName("");
      setSelectedUsers([]);
      setCreateOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEdit = () => {
    if (!selectedChat) return;
    setNewChatName(selectedChat.name || "");
    setSelectedUsers(
      (selectedChat.users || []).filter(chatUser => String(chatUser.id) !== String(user.id))
    );
    setEditOpen(true);
  };

  const handleEditChat = async () => {
    if (!selectedChatId) return;
    setEditing(true);
    try {
      const { data } = await api.put(`/internal-chats/${selectedChatId}`, {
        name: newChatName,
        userIds: selectedUsers.map(selectedUser => selectedUser.id),
      });
      setChats(prev => [data, ...prev.filter(chat => chat.id !== data.id)]);
      setNewChatName("");
      setSelectedUsers([]);
      setEditOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setEditing(false);
    }
  };

  const handleSend = async () => {
    const body = messageBody.trim();
    if ((!body && !medias.length) || !selectedChatId) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("body", body);
      medias.forEach(media => formData.append("medias", media));

      const { data } = await api.post(
        `/internal-chats/${selectedChatId}/messages`,
        formData
      );
      if (Array.isArray(data.messages)) {
        data.messages.forEach(message => dispatch({ type: "ADD_MESSAGE", payload: message }));
      } else {
        dispatch({ type: "ADD_MESSAGE", payload: data });
      }
      setMessageBody("");
      setMedias([]);
      setShowEmoji(false);
      setTimeout(() => lastMessageRef.current?.scrollIntoView({}), 100);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handleAddMedias = files => {
    if (!files?.length) return;
    setMedias(prev => [...prev, ...Array.from(files)]);
  };

  const handleRemoveMedia = index => {
    setMedias(prev => prev.filter((media, mediaIndex) => mediaIndex !== index));
  };

  const handleStartRecording = async () => {
    setSending(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error("Audio recording is not supported by this browser");
      }
      audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      audioChunks = [];
      audioRecorder = new MediaRecorder(audioStream, mimeType ? { mimeType } : undefined);
      audioRecorder.ondataavailable = event => {
        if (event.data?.size > 0) audioChunks.push(event.data);
      };
      audioRecorder.start();
      setRecording(true);
    } catch (err) {
      resetAudioRecorder();
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handleUploadAudio = async () => {
    setSending(true);
    try {
      const blob = await stopRecording();
      if (blob.size >= 10000) {
        const filename = `${new Date().getTime()}.${getAudioExtension(blob.type)}`;
        const file = new File([blob], filename, { type: blob.type });
        const formData = new FormData();
        formData.append("body", "");
        formData.append("medias", file);
        const { data } = await api.post(
          `/internal-chats/${selectedChatId}/messages`,
          formData
        );
        dispatch({ type: "ADD_MESSAGE", payload: data });
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handleCancelAudio = async () => {
    try {
      await cancelRecording();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const checkMessageMedia = message => {
    if (!message.mediaUrl) return null;
    if (message.mediaType === "image") return <ModalImageCors imageUrl={message.mediaUrl} />;
    if (message.mediaType === "audio") return <Audio url={message.mediaUrl} />;
    if (message.mediaType === "video") {
      return <video className={classes.messageMedia} src={message.mediaUrl} controls />;
    }
    return (
      <div className={classes.downloadMedia}>
        <Button startIcon={<GetApp />} color="primary" variant="outlined" href={message.mediaUrl} target="_blank">
          Download
        </Button>
      </div>
    );
  };

  const renderDailyTimestamp = (message, index) => {
    if (index === 0) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          {format(parseISO(message.createdAt), "dd/MM/yyyy")}
        </span>
      );
    }

    const currentDay = parseISO(message.createdAt);
    const previousDay = parseISO(messages[index - 1].createdAt);
    if (!isSameDay(currentDay, previousDay)) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          {format(currentDay, "dd/MM/yyyy")}
        </span>
      );
    }

    return null;
  };

  const renderMessages = () =>
    messages.map((message, index) => {
      const fromMe = String(message.userId) === String(user.id);
      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamp(message, index)}
          <div
            className={clsx(classes.messageLeft, {
              [classes.messageRight]: fromMe,
            })}
          >
            {!fromMe && <span className={classes.author}>{message.user?.name}</span>}
            {checkMessageMedia(message)}
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
            <span className={classes.timestamp}>
              {format(parseISO(message.createdAt), "HH:mm")}
            </span>
          </div>
          {index === messages.length - 1 && (
            <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />
          )}
        </React.Fragment>
      );
    });

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("internalChat.title")}</Title>
        <MainHeaderButtonsWrapper>
          {canManageChats && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setNewChatName("");
                setSelectedUsers([]);
                setCreateOpen(true);
              }}
            >
              {i18n.t("internalChat.buttons.new")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {!selectedChatId ? (
          <div className={classes.empty}>
            {loadingChats ? (
              <CircularProgress size={24} />
            ) : (
              <Typography>{i18n.t("internalChat.empty")}</Typography>
            )}
          </div>
        ) : (
          <Grid container spacing={0} className={classes.chatContainer}>
            <Grid item xs={12} md={4} className={classes.listPanel}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedChatId}
                onChange={(event, value) => setSelectedChatId(value)}
                className={classes.tabs}
              >
                {chats.map(chat => (
                  <Tab
                    key={chat.id}
                    value={String(chat.id)}
                    label={
                      <span className={classes.tabLabel}>
                        <span className={classes.tabName}>{chat.name}</span>
                        <span className={classes.tabMembers}>{getChatMembers(chat)}</span>
                      </span>
                    }
                    className={classes.tab}
                  />
                ))}
              </Tabs>
              {loadingChats && <CircularProgress size={24} />}
            </Grid>
            <Grid item xs={12} md={8} className={classes.chatPanel}>
              <div className={classes.chatHeader}>
                <Typography variant="subtitle1">{selectedChat?.name}</Typography>
                {canManageChats && (
                  <IconButton size="small" onClick={handleOpenEdit}>
                    <Edit />
                  </IconButton>
                )}
              </div>
              <div className={classes.messagesListWrapper}>
                <div
                  id="messagesList"
                  className={classes.messagesList}
                  onScroll={handleScroll}
                >
                  {renderMessages()}
                </div>
                {loadingMessages && <CircularProgress size={24} />}
              </div>
              {medias.length > 0 && (
                <div className={classes.mediaPreview}>
                  <div
                    className={classes.mediaPreviewPanel}
                    onDragOver={event => event.preventDefault()}
                    onDrop={event => {
                      event.preventDefault();
                      handleAddMedias(event.dataTransfer.files);
                    }}
                  >
                    <IconButton
                      className={classes.mediaPreviewCancel}
                      onClick={() => {
                        setMedias([]);
                        setMessageBody("");
                      }}
                      disabled={sending}
                      aria-label="cancel-upload"
                    >
                      <Cancel />
                    </IconButton>
                    <div className={classes.mediaPreviewGrid}>
                      {medias.map((media, index) => (
                        <div className={classes.mediaPreviewCard} key={`${media.name}-${index}`}>
                          <IconButton
                            size="small"
                            className={classes.mediaPreviewDelete}
                            onClick={() => handleRemoveMedia(index)}
                            disabled={sending}
                            id="deleteMediaIcon"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                          <MediaPreviewThumb file={media} className={classes.mediaPreviewImage} />
                          <p className={classes.mediaPreviewName} title={media.name}>
                            {media.name}
                          </p>
                        </div>
                      ))}
                      <label className={classes.mediaPreviewAdd} htmlFor="internal-upload-button">
                        <IconButton component="span" disabled={sending}>
                          <Add />
                        </IconButton>
                        <span>Arrastra y suelta para agregar archivos</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <div className={medias.length ? classes.mediaPreviewInputBar : classes.inputBar}>
                <IconButton
                  disabled={sending || recording}
                  onClick={() => setShowEmoji(prev => !prev)}
                >
                  <Mood />
                </IconButton>
                {showEmoji && (
                  <div className={classes.emojiBox}>
                    <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                      <Picker
                        perLine={8}
                        showPreview={false}
                        showSkinTones={false}
                        onSelect={emoji => setMessageBody(prev => prev + emoji.native)}
                      />
                    </ClickAwayListener>
                  </div>
                )}
                <input
                  multiple
                  type="file"
                  id="internal-upload-button"
                  className={classes.uploadInput}
                  onChange={event => {
                    handleAddMedias(event.target.files);
                    event.target.value = "";
                  }}
                />
                <label htmlFor="internal-upload-button">
                  <IconButton component="span" disabled={sending || recording}>
                    {medias.length ? <Add /> : <AttachFile />}
                  </IconButton>
                </label>
                <InputBase
                  className={classes.input}
                  placeholder={i18n.t("internalChat.inputPlaceholder")}
                  value={messageBody}
                  onChange={event => setMessageBody(event.target.value)}
                  onKeyPress={event => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  multiline
                  maxRows={4}
                  disabled={sending || recording}
                  onPaste={event => {
                    if (event.clipboardData.files[0]) {
                      handleAddMedias(event.clipboardData.files);
                    }
                  }}
                />
                {messageBody.trim() || medias.length ? (
                  <IconButton color="primary" disabled={sending} onClick={handleSend}>
                    <Send />
                  </IconButton>
                ) : recording ? (
                  <div className={classes.recorderWrapper}>
                    <IconButton disabled={sending} onClick={handleCancelAudio}>
                      <HighlightOff className={classes.cancelAudioIcon} />
                    </IconButton>
                    {sending ? <CircularProgress size={24} /> : <RecordingTimer />}
                    <IconButton disabled={sending} onClick={handleUploadAudio}>
                      <CheckCircleOutline className={classes.sendAudioIcon} />
                    </IconButton>
                  </div>
                ) : (
                  <IconButton disabled={sending} onClick={handleStartRecording}>
                    <Mic />
                  </IconButton>
                )}
              </div>
            </Grid>
          </Grid>
        )}
      </Paper>
      <Dialog
        open={createOpen || editOpen}
        onClose={() => { setCreateOpen(false); setEditOpen(false); }}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {editOpen
            ? i18n.t("internalChat.edit.title")
            : i18n.t("internalChat.create.title")}
        </DialogTitle>
        <DialogContent dividers className={classes.createContent}>
          <TextField
            label={i18n.t("internalChat.create.name")}
            value={newChatName}
            onChange={event => setNewChatName(event.target.value)}
            variant="outlined"
            margin="dense"
            autoFocus
          />
          <Autocomplete
            multiple
            options={users.filter(option => String(option.id) !== String(user.id))}
            getOptionLabel={option => option.name || ""}
            value={selectedUsers}
            onChange={(event, value) => setSelectedUsers(value)}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                margin="dense"
                label={i18n.t("internalChat.create.users")}
              />
            )}
          />
        </DialogContent>
        <DialogActions className={classes.createDialogActions}>
          <Button onClick={() => { setCreateOpen(false); setEditOpen(false); }} disabled={creating || editing}>
            {i18n.t("internalChat.buttons.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={editOpen ? handleEditChat : handleCreateChat}
            disabled={creating || editing || !newChatName.trim()}
          >
            {editOpen
              ? i18n.t("internalChat.buttons.save")
              : i18n.t("internalChat.buttons.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default InternalChat;

